import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useEffect } from "react";
import { AuthProvider } from "./contexts/AuthContext";
import { PublicSettingsProvider } from "./contexts/PublicSettingsContext";
import { LanguageProvider } from "./contexts/LanguageContext";
import { ProtectedRoute, PublicOnlyRoute } from "./components/ProtectedRoute";

// Public Layout Components
import { Header } from "./components/Header";
import { Footer } from "./components/Footer";

// Public Pages
import { Home } from "./pages/Home";
import { Courses } from "./pages/Courses";
import { Pricing } from "./pages/Pricing";
import { LevelTest } from "./pages/LevelTest";
import { About } from "./pages/About";
import { Testimonials } from "./pages/Testimonials";
import { TestimonialDetail } from "./pages/TestimonialDetail";
import { FAQ } from "./pages/FAQ";
import { Contact } from "./pages/Contact";
import { BookTrial } from "./pages/BookTrial";
import { PrivacyPolicy } from "./pages/PrivacyPolicy";
import { TermsOfService } from "./pages/TermsOfService";

// Auth Pages
import { Login } from "./pages/auth/Login";
import { Register } from "./pages/auth/Register";

// Admin Pages
import { AdminLayout } from "./pages/admin/AdminLayout";
import { AdminDashboard } from "./pages/admin/AdminDashboard";
import { AdminStudents } from "./pages/admin/AdminStudents";
import { AdminGroups } from "./pages/admin/AdminGroups";
import { AdminClasses } from "./pages/admin/AdminClasses";
import { AdminActivities } from "./pages/admin/AdminActivities";
import { AdminLeads } from "./pages/admin/AdminLeads";
import { AdminMessages } from "./pages/admin/AdminMessages";
import { AdminChat } from "./pages/admin/AdminChat";
import { AdminSettings } from "./pages/admin/AdminSettings";
import { AdminTeachers } from "./pages/admin/AdminTeachers";
import { AdminCMS } from "./pages/admin/AdminCMS";
import { AdminMedia } from "./pages/admin/AdminMedia";
import { AdminAvailability } from "./pages/admin/AdminAvailability";
import { AdminDocumentation } from "./pages/admin/AdminDocumentation";
import { AdminOnboarding } from "./pages/admin/AdminOnboarding";
import { AdminPlans } from "./pages/admin/AdminPlans";
import { AdminBilling } from "./pages/admin/AdminBilling";
import { AdminCoupons } from "./pages/admin/AdminCoupons";
import { AdminLevelTestQuestions } from "./pages/admin/AdminLevelTestQuestions";
import { AdminTestimonials } from "./pages/admin/AdminTestimonials";
import { OnboardingGuard } from "./components/OnboardingGuard";
import { StudentOnboardingGuard } from "./components/StudentOnboardingGuard";
import { StudentOnboarding } from "./pages/student/StudentOnboarding";

// Student Pages
import { StudentLayout } from "./pages/student/StudentLayout";
import { StudentDashboard } from "./pages/student/StudentDashboard";
import { StudentPending } from "./pages/student/StudentPending";
import { StudentClasses } from "./pages/student/StudentClasses";
import { StudentSchedule } from "./pages/student/StudentSchedule";
import { StudentAvailableClasses } from "./pages/student/StudentAvailableClasses";
import { StudentActivities } from "./pages/student/StudentActivities";
import { StudentMessages } from "./pages/student/StudentMessages";
import { StudentChat } from "./pages/student/StudentChat";
import { StudentProfile } from "./pages/student/StudentProfile";
import { StudentBilling } from "./pages/student/StudentBilling";
import { StudentCheckout } from "./pages/student/StudentCheckout";
import { StudentCheckoutSuccess } from "./pages/student/StudentCheckoutSuccess";
import { StudentLevelTest } from "./pages/student/StudentLevelTest";

import { DocsTeacher } from "./pages/docs/DocsTeacher";
import { DocsStudent } from "./pages/docs/DocsStudent";
import { DocsParent } from "./pages/docs/DocsParent";

import { Toaster } from "./components/ui/sonner";
import { useLocation } from "react-router-dom";

// Public Layout Wrapper
const PublicLayout = ({ children }) => (
  <>
    <Header />
    {children}
    <Footer />
  </>
);

const ScrollToTop = () => {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return null;
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <LanguageProvider>
        <PublicSettingsProvider>
          <AuthProvider>
            <Routes>
              {/* Public Pages with Header/Footer */}
              <Route path="/" element={<PublicLayout><Home /></PublicLayout>} />
              <Route path="/courses" element={<PublicLayout><Courses /></PublicLayout>} />
              <Route path="/pricing" element={<PublicLayout><Pricing /></PublicLayout>} />
              <Route path="/level-test" element={<PublicLayout><LevelTest /></PublicLayout>} />
              <Route path="/about" element={<PublicLayout><About /></PublicLayout>} />
              <Route path="/testimonials" element={<PublicLayout><Testimonials /></PublicLayout>} />
              <Route path="/testimonials/:slug" element={<PublicLayout><TestimonialDetail /></PublicLayout>} />
              <Route path="/faq" element={<PublicLayout><FAQ /></PublicLayout>} />
              <Route path="/contact" element={<PublicLayout><Contact /></PublicLayout>} />
              <Route path="/book-trial" element={<PublicLayout><BookTrial /></PublicLayout>} />
              {/* F5: orphaned page removed — nothing in-app links here, and PublicOnlyRoute
                  on /login already bounces an already-authenticated visitor to their
                  real dashboard, so this covers every case the old page tried to. */}
              <Route path="/student-access" element={<Navigate to="/login" replace />} />
              <Route path="/privacy" element={<PublicLayout><PrivacyPolicy /></PublicLayout>} />
              <Route path="/terms" element={<PublicLayout><TermsOfService /></PublicLayout>} />
              <Route path="/docs/teacher" element={<DocsTeacher />} />
              <Route path="/docs/student" element={<DocsStudent />} />
              <Route path="/docs/parent" element={<DocsParent />} />

              {/* Auth Pages (No Header/Footer) */}
              <Route path="/login" element={
                <PublicOnlyRoute>
                  <Login />
                </PublicOnlyRoute>
              } />
              <Route path="/register" element={
                <PublicOnlyRoute>
                  <Register />
                </PublicOnlyRoute>
              } />

              {/* Admin Onboarding — standalone, no AdminLayout sidebar */}
              <Route path="/admin/onboarding" element={
                <ProtectedRoute requireAdmin requireStrictAdmin>
                  <AdminOnboarding />
                </ProtectedRoute>
              } />

              {/* Student Onboarding — standalone, no StudentLayout sidebar */}
              <Route path="/student/onboarding" element={
                <ProtectedRoute>
                  <StudentOnboarding />
                </ProtectedRoute>
              } />

              {/* Admin Routes */}
              <Route path="/admin" element={
                <ProtectedRoute requireAdmin>
                  <OnboardingGuard>
                    <AdminLayout />
                  </OnboardingGuard>
                </ProtectedRoute>
              }>
                <Route index element={<AdminDashboard />} />
                <Route path="dashboard" element={<AdminDashboard />} />
                <Route path="leads" element={<AdminLeads />} />
                <Route path="students" element={<AdminStudents />} />
                <Route path="teachers" element={
                  <ProtectedRoute requireAdmin requireStrictAdmin>
                    <AdminTeachers />
                  </ProtectedRoute>
                } />
                <Route path="groups" element={<AdminGroups />} />
                <Route path="classes" element={<AdminClasses />} />
                <Route path="calendar" element={<AdminClasses />} />
                <Route path="activities" element={<AdminActivities />} />
                <Route path="contacts" element={<AdminMessages />} />
                <Route path="messages" element={<AdminMessages />} />
                <Route path="chat" element={<AdminChat />} />
                <Route path="media" element={
                  <ProtectedRoute requireAdmin requireStrictAdmin>
                    <AdminMedia />
                  </ProtectedRoute>
                } />
                <Route path="cms" element={
                  <ProtectedRoute requireAdmin requireStrictAdmin>
                    <AdminCMS />
                  </ProtectedRoute>
                } />
                <Route path="site-content" element={
                  <ProtectedRoute requireAdmin requireStrictAdmin>
                    <AdminCMS />
                  </ProtectedRoute>
                } />
                <Route path="availability" element={
                  <ProtectedRoute requireAdmin requireStrictAdmin>
                    <AdminAvailability />
                  </ProtectedRoute>
                } />
                <Route path="plans" element={
                  <ProtectedRoute requireAdmin requireStrictAdmin>
                    <AdminPlans />
                  </ProtectedRoute>
                } />
                <Route path="billing" element={
                  <ProtectedRoute requireAdmin requireStrictAdmin>
                    <AdminBilling />
                  </ProtectedRoute>
                } />
                <Route path="coupons" element={
                  <ProtectedRoute requireAdmin requireStrictAdmin>
                    <AdminCoupons />
                  </ProtectedRoute>
                } />
                <Route path="documentation" element={<AdminDocumentation />} />
                <Route path="settings" element={
                  <ProtectedRoute requireAdmin requireStrictAdmin>
                    <AdminSettings />
                  </ProtectedRoute>
                } />
                <Route path="level-test" element={
                  <ProtectedRoute requireAdmin requireStrictAdmin>
                    <AdminLevelTestQuestions />
                  </ProtectedRoute>
                } />
                <Route path="testimonials" element={
                  <ProtectedRoute requireAdmin requireStrictAdmin>
                    <AdminTestimonials />
                  </ProtectedRoute>
                } />
              </Route>

              {/* Student Routes */}
              <Route path="/student/pending" element={
                <ProtectedRoute>
                  <StudentPending />
                </ProtectedRoute>
              } />
              <Route path="/student/checkout" element={
                <ProtectedRoute>
                  <StudentCheckout />
                </ProtectedRoute>
              } />
              <Route path="/student/checkout/success" element={
                <ProtectedRoute>
                  <StudentCheckoutSuccess />
                </ProtectedRoute>
              } />
              <Route path="/student/level-test" element={
                <ProtectedRoute>
                  <StudentLevelTest />
                </ProtectedRoute>
              } />
              <Route path="/student" element={
                <ProtectedRoute>
                  <StudentOnboardingGuard>
                    <StudentLayout />
                  </StudentOnboardingGuard>
                </ProtectedRoute>
              }>
                <Route index element={<StudentDashboard />} />
                <Route path="dashboard" element={<StudentDashboard />} />
                <Route path="classes" element={<StudentClasses />} />
                <Route path="schedule" element={<StudentSchedule />} />
                <Route path="available" element={<StudentAvailableClasses />} />
                <Route path="activities" element={<StudentActivities />} />
                <Route path="billing" element={<StudentBilling />} />
                <Route path="messages" element={<StudentMessages />} />
                <Route path="chat" element={<StudentChat />} />
                <Route path="profile" element={<StudentProfile />} />
              </Route>
              {/* 404 catch-all */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            <Toaster />
          </AuthProvider>
        </PublicSettingsProvider>
        </LanguageProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
